import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TabNavigationService } from '~/services/tab-navigation.service';

@Component({
    selector: 'app-chat-input',
    templateUrl: './chat-input.component.html',
    styleUrls: ['./chat-input.component.scss'],
    standalone: false
})
export class ChatInputComponent implements OnInit, OnDestroy {
    @Input() promptText: string = '';
    @Input() promptPlaceholder: string = 'Ask IntelENGINE';
    @Input() selectedFiles: string[] = [];
    @Input() disabled: boolean = false;
    @Input() isChatMode: boolean = false;
    @Input() fileUploadEnabled: boolean = false;

    @Output() submitMessage = new EventEmitter<void>();
    @Output() fileSelected = new EventEmitter<FileList>();
    @Output() fileRemoved = new EventEmitter<number>();
    @Output() promptChange = new EventEmitter<string>();

    public generateDrtTable: boolean = false;
    private destroy$ = new Subject<void>();
    private pasteCounter: number = 0;

    constructor(private tabNavigationService: TabNavigationService) {}

    public ngOnInit(): void {
        this.tabNavigationService.searchSettings$
            .pipe(takeUntil(this.destroy$))
            .subscribe((settings) => {
                this.generateDrtTable = !!settings.generateDrtTable;
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // Handle text input changes
    public onTextInput(event: Event): void {
        const textarea = event.target as HTMLTextAreaElement;
        this.promptChange.emit(textarea.value);
    }

    // Handle Enter key press
    public onEnterKey(event: KeyboardEvent): void {
        // If Shift+Enter, allow new line
        if (event.shiftKey) {
            return;
        }
        
        // Otherwise, prevent new line and submit
        event.preventDefault();
        this.onSubmit();
    }

    // Handle clipboard paste — captures pasted images and emits them as a FileList
    public onPaste(event: ClipboardEvent): void {
        if (this.disabled) {
            return;
        }

        const items = event.clipboardData?.items;
        if (!items) {
            return;
        }

        const imageItem = Array.from(items).find(item => item.type.startsWith('image/'));
        if (!imageItem) {
            // No image in clipboard — let default text paste proceed
            return;
        }

        event.preventDefault();

        const file = imageItem.getAsFile();
        if (!file) {
            return;
        }

        // Give the pasted image a human-readable name: Attachment 1, Attachment 2, …
        this.pasteCounter++;
        const extension = file.type.split('/')[1] || 'png';
        const pastedFileName = `Attachment ${this.pasteCounter}.${extension}`;
        const renamedFile = new File([file], pastedFileName, { type: file.type });

        const dt = new DataTransfer();
        dt.items.add(renamedFile);
        this.fileSelected.emit(dt.files);
    }

    // Handle file selection
    public onFileSelected(event: Event): void {
        if (this.disabled) {
            return;
        }

        const input = event.target as HTMLInputElement;
        if (input.files) {
            this.fileSelected.emit(input.files);
            // Reset input
            input.value = '';
        }
    }

    // Remove file
    public removeFile(index: number): void {
        this.fileRemoved.emit(index);
    }

    public onGenerateDrtTableChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.tabNavigationService.updateSearchSettings({
            generateDrtTable: !!input.checked
        });
    }

    // Submit message
    public onSubmit(): void {
        if (this.promptText.trim().length === 0 && this.selectedFiles.length === 0) {
            return;
        }

        this.submitMessage.emit();
    }
}
